import type { QuizQuestion } from '../types';

export const CAREER_QUIZ_QUESTIONS: QuizQuestion[] = [
  // --- RESUME & ATS BEST PRACTICES (1 - 25) ---
  {
    id: 1,
    question: "What does ATS stand for in modern hiring processes?",
    options: [
      "Applicant Tracking System",
      "Automated Talent Scanner",
      "Application Technical Standard",
      "Advanced Team Sourcing"
    ],
    correctIndex: 0,
    explanation: "ATS stands for Applicant Tracking System. It is software used by recruiters to organize, scan, and rank job applications.",
    category: "Resume"
  },
  {
    id: 2,
    question: "What is the recommended maximum page length for a professional with under 5 years of experience?",
    options: [
      "1 page",
      "2 pages",
      "3 pages",
      "Length does not matter"
    ],
    correctIndex: 0,
    explanation: "For early to mid-career professionals, a crisp 1-page resume is industry standard and gets read faster.",
    category: "Resume"
  },
  {
    id: 3,
    question: "Which of the following resume file formats is safest for ATS scanning unless specified otherwise?",
    options: [
      "PDF or DOCX",
      "JPEG image",
      "PNG image",
      "HTML web file"
    ],
    correctIndex: 0,
    explanation: "Clean PDFs or standard Microsoft Word (.docx) files are easily parsed by modern ATS algorithms.",
    category: "Resume"
  },
  {
    id: 4,
    question: "What is the best way to format bullet points under work experience?",
    options: [
      "Action Verb + Task + Quantifiable Result",
      "Paragraph of daily duties",
      "List of generic responsibilities",
      "Company mission statement"
    ],
    correctIndex: 0,
    explanation: "Using the Action Verb + Context + Measurable Result framework (e.g. 'Increased sales by 25%...') proves impact.",
    category: "Resume"
  },
  {
    id: 5,
    question: "Should you include your full home address on a modern resume?",
    options: [
      "No, City and State/Country is sufficient for privacy and location targeting",
      "Yes, full street address is mandatory",
      "Yes, including zip code and apartment number",
      "No, do not list location at all"
    ],
    correctIndex: 0,
    explanation: "Listing City, State (or Country) protects privacy while letting recruiters know your geographic location.",
    category: "Resume"
  },
  {
    id: 6,
    question: "Why should you avoid putting key contact details in the resume header/footer in older ATS systems?",
    options: [
      "Some legacy ATS software fails to parse text inside header/footer areas",
      "Headers increase document size",
      "Footers print in low resolution",
      "It causes font corruption"
    ],
    correctIndex: 0,
    explanation: "Legacy ATS parsers often ignore document headers/footers, so contact info should be placed in the body text.",
    category: "Resume"
  },
  {
    id: 7,
    question: "What font size range is recommended for main resume body text?",
    options: [
      "10pt to 12pt",
      "6pt to 8pt",
      "14pt to 16pt",
      "18pt to 20pt"
    ],
    correctIndex: 0,
    explanation: "Body text between 10pt and 12pt ensures clean readability for recruiters without straining eyes.",
    category: "Resume"
  },
  {
    id: 8,
    question: "What is the primary purpose of a Resume Summary statement at the top of your resume?",
    options: [
      "To quickly hook recruiters with your top skills and relevant career achievements",
      "To list your personal hobbies and interests",
      "To explain why you left your previous jobs",
      "To demand a specific salary expectation"
    ],
    correctIndex: 0,
    explanation: "A strong 2-3 line summary instantly communicates your value proposition and core strengths.",
    category: "Resume"
  },
  {
    id: 9,
    question: "Which tense should you use for bullet points describing your current job role?",
    options: [
      "Present tense (e.g., Manage, Develop)",
      "Past tense (e.g., Managed, Developed)",
      "Future tense (e.g., Will manage)",
      "Passive voice (e.g., Was managing)"
    ],
    correctIndex: 0,
    explanation: "Use present tense verbs for current ongoing duties and past tense verbs for past achievements or past jobs.",
    category: "Resume"
  },
  {
    id: 10,
    question: "What are 'Resume Keywords'?",
    options: [
      "Specific skills, tools, and job terms from the job description that match ATS filters",
      "Fancy vocabulary words used to sound clever",
      "Passwords required to unlock locked PDF resumes",
      "Tags placed in document metadata"
    ],
    correctIndex: 0,
    explanation: "Keywords are critical skills and qualifications directly quoted or matched from the target job posting.",
    category: "Resume"
  },
  {
    id: 11,
    question: "Is it good practice to include graphics, tables, or charts inside an ATS-formatted resume?",
    options: [
      "No, ATS parsers often scramble or fail to read text embedded in tables or charts",
      "Yes, graphics always boost your ATS score",
      "Yes, ATS algorithms prefer pie charts over text",
      "It makes no difference"
    ],
    correctIndex: 0,
    explanation: "Complex tables, text boxes, and images can cause ATS software to misread or skip information.",
    category: "Resume"
  },
  {
    id: 12,
    question: "How should work history be listed chronologically on a standard resume?",
    options: [
      "Reverse-chronological order (most recent position first)",
      "Chronological order (first job ever at the top)",
      "Alphabetical order by company name",
      "Order of salary level"
    ],
    correctIndex: 0,
    explanation: "Reverse-chronological order is the recruiter-favorite format because it highlights recent experience.",
    category: "Resume"
  },
  {
    id: 13,
    question: "What is an ATS 'Hard Skill' vs 'Soft Skill'?",
    options: [
      "Hard skills are technical proficiencies (e.g. Python, Financial Modeling); Soft skills are interpersonal (e.g. Communication)",
      "Hard skills are difficult to learn; Soft skills are easy",
      "Hard skills are for managers; Soft skills are for interns",
      "There is no practical difference"
    ],
    correctIndex: 0,
    explanation: "Hard skills quantify technical capability; soft skills demonstrate work style and collaboration.",
    category: "Resume"
  },
  {
    id: 14,
    question: "What should you do if you have employment gaps on your resume?",
    options: [
      "Focus on achievements, freelance projects, upskilling courses, or relevant personal growth during that period",
      "Fabricate fake employment dates to cover the gap",
      "Leave dates off the entire resume",
      "Write a long essay explaining personal details"
    ],
    correctIndex: 0,
    explanation: "Highlighting freelance work, certifications, volunteering, or continuous learning frames gaps constructively.",
    category: "Resume"
  },
  {
    id: 15,
    question: "Why should you tailor your resume for every job application?",
    options: [
      "To align your highlighted skills with the specific priorities and ATS keywords of that job description",
      "Because recruiters require 10 different versions of your experience",
      "To trick ATS software into giving you 100%",
      "Tailoring is unnecessary"
    ],
    correctIndex: 0,
    explanation: "Tailoring ensures your most relevant accomplishments match the employer's specific requirements.",
    category: "Resume"
  },
  {
    id: 16,
    question: "What is a 'Functional Resume' and when is it typically discouraged?",
    options: [
      "It focuses on skills rather than chronology; recruiters often dislike it because it masks career timelines",
      "It is a resume written in code",
      "It is a 5-page detailed resume",
      "It is a portfolio website"
    ],
    correctIndex: 0,
    explanation: "Functional resumes group experience by skill rather than company/date, which can raise red flags for recruiters.",
    category: "Resume"
  },
  {
    id: 17,
    question: "Should you include references directly on your modern resume?",
    options: [
      "No, omit 'References available upon request' to save valuable space",
      "Yes, list 5 personal contact numbers",
      "Yes, mandatory at the bottom of page 1",
      "Only list family members"
    ],
    correctIndex: 0,
    explanation: "Recruiters assume references are available when requested; omitting them saves valuable resume real estate.",
    category: "Resume"
  },
  {
    id: 18,
    question: "What is the recommended margin size for a standard resume?",
    options: [
      "0.5 inches to 1 inch",
      "0.1 inches (as tight as possible)",
      "2 inches wide",
      "Variable per line"
    ],
    correctIndex: 0,
    explanation: "Margins between 0.5 and 1 inch maintain visual balance, readability, and neat printing bounds.",
    category: "Resume"
  },
  {
    id: 19,
    question: "Which action verb is strongest for a project management bullet point?",
    options: [
      "Spearheaded",
      "Helped with",
      "Was involved in",
      "Tried to do"
    ],
    correctIndex: 0,
    explanation: "Strong verbs like 'Spearheaded', 'Orchestrated', or 'Engineered' convey decisive leadership and action.",
    category: "Resume"
  },
  {
    id: 20,
    question: "What is the 'Rule of 6 Seconds' in resume screening?",
    options: [
      "Recruiters spend an average of 6 to 7 seconds scanning a resume initially before deciding to read further",
      "ATS systems delete resumes after 6 seconds",
      "You must submit applications within 6 seconds of posting",
      "It takes 6 seconds to create a resume with AI"
    ],
    correctIndex: 0,
    explanation: "Studies show recruiters skim a resume for 6-7 seconds to find key titles, metrics, and skills.",
    category: "Resume"
  },
  {
    id: 21,
    question: "Where should your Education section be placed on your resume if you are a mid-career professional?",
    options: [
      "Below Work Experience",
      "At the very top before Summary",
      "In a sidebar footer",
      "Omitted entirely"
    ],
    correctIndex: 0,
    explanation: "Experienced professionals should prioritize relevant Work Experience at the top and place Education below.",
    category: "Resume"
  },
  {
    id: 22,
    question: "When is it appropriate to place Education above Work Experience on a resume?",
    options: [
      "When you are a current student or recent graduate with limited professional work history",
      "Only if you went to an Ivy League university",
      "When changing careers to an unrelated field after 15 years",
      "Never"
    ],
    correctIndex: 0,
    explanation: "Students and fresh grads leverage academic coursework, honors, and degrees first to establish credentials.",
    category: "Resume"
  },
  {
    id: 23,
    question: "What should you check before submitting your resume as a final PDF file?",
    options: [
      "Proofread for typos, verify formatting, test clickable links, and ensure ATS text selectability",
      "Add background music",
      "Change text color to bright yellow",
      "Encrypt with password"
    ],
    correctIndex: 0,
    explanation: "Ensuring text is selectable (not flattened image text) and free of typos is essential before sending.",
    category: "Resume"
  },
  {
    id: 24,
    question: "What is the main benefit of listing certifications on your resume?",
    options: [
      "It proves verifiable industry standards and commitment to professional upskilling",
      "It replaces work experience entirely",
      "It guarantees immediate higher salary",
      "It satisfies ATS length requirements"
    ],
    correctIndex: 0,
    explanation: "Industry certifications (e.g. AWS, PMP, Scrum) demonstrate concrete, verified expertise.",
    category: "Resume"
  },
  {
    id: 25,
    question: "Why are hyperlinks (e.g. LinkedIn profile, GitHub, Portfolio) valuable on modern resumes?",
    options: [
      "They allow hiring managers to quickly view live proof of your work and professional profile with one click",
      "They help ATS bypass security firewalls",
      "They increase page count automatically",
      "They add decorative underline styling"
    ],
    correctIndex: 0,
    explanation: "Clean hyperlinked profiles enable seamless verification of your portfolio and online presence.",
    category: "Resume"
  },

  // --- COVER LETTERS (26 - 40) ---
  {
    id: 26,
    question: "What is the recommended length of a professional cover letter?",
    options: [
      "3 to 4 paragraphs (half page to 1 page maximum)",
      "3 to 4 pages",
      "1 single sentence",
      "Length does not matter"
    ],
    correctIndex: 0,
    explanation: "A concise 250-400 word cover letter respecting the reader's time is most impactful.",
    category: "Cover Letter"
  },
  {
    id: 27,
    question: "How should you ideally address the greeting of a cover letter?",
    options: [
      "To the specific Hiring Manager or Department Leader by name if findable",
      "'Dear Sir/Madam'",
      "'To Whom It May Concern'",
      "'Hey there hiring team!'"
    ],
    correctIndex: 0,
    explanation: "Addressing the actual hiring manager shows initiative and personalized effort.",
    category: "Cover Letter"
  },
  {
    id: 28,
    question: "What should the opening paragraph of a cover letter state immediately?",
    options: [
      "The specific role you are applying for and why you are enthusiastic about the company",
      "Your desired compensation and benefit demands",
      "A summary of your life story",
      "Complaints about your previous employer"
    ],
    correctIndex: 0,
    explanation: "State the target position right away and hook the manager with authentic enthusiasm.",
    category: "Cover Letter"
  },
  {
    id: 29,
    question: "What is the core focus of the body paragraphs in a cover letter?",
    options: [
      "Connecting your key achievements to the company's specific challenges and needs",
      "Repeating every line from your resume word-for-word",
      "Explaining your personal financial goals",
      "Listing all skills in alphabetical order"
    ],
    correctIndex: 0,
    explanation: "The body should tell the story of HOW your achievements solve the employer's specific problems.",
    category: "Cover Letter"
  },
  {
    id: 30,
    question: "How should you conclude a cover letter in the closing paragraph?",
    options: [
      "Reiterate interest, request an interview call, and thank the reader for their time",
      "Demand an immediate decision within 24 hours",
      "Say nothing and sign your name",
      "Include a disclaimer about background checks"
    ],
    correctIndex: 0,
    explanation: "Close with a confident call-to-action (requesting a interview meeting) and polite gratitude.",
    category: "Cover Letter"
  },
  {
    id: 31,
    question: "Should your cover letter use the same visual header and typography as your resume?",
    options: [
      "Yes, matching personal branding creates a polished, cohesive application package",
      "No, use completely different fonts and colors to stand out",
      "No, cover letters must be plain unformatted text files",
      "It does not matter"
    ],
    correctIndex: 0,
    explanation: "Consistent headers, fonts, and styling tie your resume and letter together professionally.",
    category: "Cover Letter"
  },
  {
    id: 32,
    question: "Is it effective to copy-paste a generic cover letter for 50 different applications?",
    options: [
      "No, generic letters are easily spotted and often discarded by recruiters",
      "Yes, generic letters save time with zero impact on callback rate",
      "Yes, ATS algorithms rank generic letters higher",
      "Generic letters are mandatory"
    ],
    correctIndex: 0,
    explanation: "Customizing even 2-3 sentences to reference company projects or culture dramatically raises response rates.",
    category: "Cover Letter"
  },
  {
    id: 33,
    question: "What should you avoid mentioning in a cover letter?",
    options: [
      "Negative comments about past employers or managers",
      "Your relevant technical achievements",
      "Why you like the company's product",
      "Your preferred contact details"
    ],
    correctIndex: 0,
    explanation: "Maintain a positive, constructive professional tone; never vent about past workplaces.",
    category: "Cover Letter"
  },
  {
    id: 34,
    question: "When applying via email, where should the cover letter text usually be placed?",
    options: [
      "In the body of the email (or attached as PDF if requested by job portal)",
      "As a ZIP file",
      "In the email Subject line",
      "Linked in a Google Drive folder"
    ],
    correctIndex: 0,
    explanation: "Pasting the letter in the email body makes it instantly readable upon opening.",
    category: "Cover Letter"
  },
  {
    id: 35,
    question: "What is a 'T-Chart' cover letter format?",
    options: [
      "A format comparing 'What You Need' in one column with 'How I Match It' in another",
      "A cover letter written in shape of the letter T",
      "A technical chart of software tools",
      "A timeline chart of work history"
    ],
    correctIndex: 0,
    explanation: "T-Chart cover letters clearly show side-by-side alignment between job requirements and your qualifications.",
    category: "Cover Letter"
  },
  {
    id: 36,
    question: "How can you research a company to personalize your cover letter effectively?",
    options: [
      "Read recent company blog posts, news announcements, product launches, or mission values",
      "Guess their upcoming revenue targets",
      "Call reception and ask for secrets",
      "Copy news articles verbatim"
    ],
    correctIndex: 0,
    explanation: "Referencing recent company initiatives proves genuine interest and proactive initiative.",
    category: "Cover Letter"
  },
  {
    id: 37,
    question: "What is the best sign-off phrase for a formal business letter?",
    options: [
      "Sincerely / Best regards,",
      "Cheers,",
      "Later,",
      "Yours forever,"
    ],
    correctIndex: 0,
    explanation: "'Sincerely' or 'Best regards' remains timeless and professional for business correspondence.",
    category: "Cover Letter"
  },
  {
    id: 38,
    question: "Why should you mention referral names in your cover letter opening if you have one?",
    options: [
      "Referrals establish immediate trust and context through a shared contact",
      "Referrals legally force an interview",
      "Referrals boost ATS ranking score automatically",
      "Referrals replace resume screening"
    ],
    correctIndex: 0,
    explanation: "Dropping a mutual contact name in sentence one immediately grabs hiring attention.",
    category: "Cover Letter"
  },
  {
    id: 39,
    question: "What tone is recommended for modern tech and startup cover letters?",
    options: [
      "Professional yet engaging, authentic, and conversational",
      "Extremely stiff, archaic 19th-century legal language",
      "Casual slang and informal emojis",
      "Arrogant and demanding"
    ],
    correctIndex: 0,
    explanation: "Modern tech cultures appreciate clear, human, direct communication over robotic formality.",
    category: "Cover Letter"
  },
  {
    id: 40,
    question: "Does an AI cover letter generator replace your personal touch?",
    options: [
      "AI provides a strong first draft structure, but you should always edit and refine personal details",
      "No, AI generated text requires zero editing",
      "AI text is prohibited in hiring",
      "AI writes better personal stories than humans"
    ],
    correctIndex: 0,
    explanation: "Use AI to outline and polish, then customize with your authentic experiences and voice.",
    category: "Cover Letter"
  },

  // --- INTERVIEW STRATEGIES & STAR METHOD (41 - 65) ---
  {
    id: 41,
    question: "What does the STAR method stand for in behavioral interview answers?",
    options: [
      "Situation, Task, Action, Result",
      "Strategy, Talent, Assessment, Recommendation",
      "System, Test, Analysis, Review",
      "Solution, Target, Achievement, Reaction"
    ],
    correctIndex: 0,
    explanation: "STAR is the golden framework for answering behavioral interview questions concisely.",
    category: "Interview"
  },
  {
    id: 42,
    question: "Which component of the STAR method should take up the largest portion of your answer?",
    options: [
      "Action (what specific steps YOU took)",
      "Situation (background story)",
      "Task (the challenge given)",
      "All parts should be equal length"
    ],
    correctIndex: 0,
    explanation: "Focus 50-60% of your answer on Action — explaining your decision-making and execution.",
    category: "Interview"
  },
  {
    id: 43,
    question: "How should you answer the common opening question: 'Tell me about yourself'?",
    options: [
      "Give a 2-minute elevator pitch covering Present role, Past key achievements, and Future alignment",
      "Recite your full biography starting from childhood",
      "Ask the interviewer to read your resume instead",
      "Discuss personal details and hobbies exclusively"
    ],
    correctIndex: 0,
    explanation: "The Present-Past-Future model provides a focused, professional 2-minute summary.",
    category: "Interview"
  },
  {
    id: 44,
    question: "When asked about your greatest weakness, what is the best strategic approach?",
    options: [
      "Mention a real past weakness you actively worked to improve, showing self-awareness and progress",
      "Say 'I am a perfectionist and work too hard'",
      "Say 'I don't have any weaknesses'",
      "List critical requirements of the role as weaknesses"
    ],
    correctIndex: 0,
    explanation: "Demonstrating genuine self-awareness and active growth measures builds authentic credibility.",
    category: "Interview"
  },
  {
    id: 45,
    question: "What should you prepare before going into any job interview?",
    options: [
      "3-5 STAR stories, company background research, role analysis, and 3 intelligent questions to ask them",
      "Nothing, spontaneity is best",
      "Memorize 100 scripted answers word-for-word",
      "Just compensation demands"
    ],
    correctIndex: 0,
    explanation: "Preparation boosts confidence; knowing the company and having flexible STAR stories is key.",
    category: "Interview"
  },
  {
    id: 46,
    question: "What is the recommended rule when the interviewer asks: 'Do you have any questions for us?'",
    options: [
      "Always ask 2 to 3 insightful questions about company culture, team goals, or upcoming projects",
      "Say 'No, you answered everything'",
      "Ask 'How much vacation time do I get starting week 1?'",
      "Ask 'When do I get a promotion?'"
    ],
    correctIndex: 0,
    explanation: "Asking intelligent questions demonstrates deep interest, critical thinking, and engagement.",
    category: "Interview"
  },
  {
    id: 47,
    question: "What is an excellent question to ask the interviewer at the end of an interview?",
    options: [
      "'What does success look like in this role during the first 90 days?'",
      "'How often do people call in sick here?'",
      "'Do you track employee computer activity?'",
      "'Will I get an office with a window?'"
    ],
    correctIndex: 0,
    explanation: "Asking about 90-day success metrics highlights performance focus and results orientation.",
    category: "Interview"
  },
  {
    id: 48,
    question: "How should you handle a technical question when you do NOT know the exact answer?",
    options: [
      "Be honest, explain how you would approach finding the answer or solving it logically step-by-step",
      "Pretend to know and make up fake technical terms",
      "Change the subject abruptly",
      "Blame the question for being unclear"
    ],
    correctIndex: 0,
    explanation: "Honesty combined with clear problem-solving logic displays integrity and analytical skill.",
    category: "Interview"
  },
  {
    id: 49,
    question: "What should you test 15 minutes before a virtual video interview?",
    options: [
      "Camera, microphone, internet stability, lighting, background visual, and app updates",
      "Nothing if it worked yesterday",
      "Virtual party filters",
      "Audio volume on max"
    ],
    correctIndex: 0,
    explanation: "Pre-testing technical equipment prevents last-minute panic or embarrassing tech glitches.",
    category: "Interview"
  },
  {
    id: 50,
    question: "Where should your eyes focus during a virtual video interview to maintain eye contact?",
    options: [
      "Directly into the webcam lens while speaking",
      "At your own video preview thumbnail",
      "At the bottom corner of your screen",
      "Looking away at ceiling"
    ],
    correctIndex: 0,
    explanation: "Looking at the webcam lens simulates direct eye contact for the interviewer on their screen.",
    category: "Interview"
  },
  {
    id: 51,
    question: "What is the 'PAR' method variant of behavioral interviewing?",
    options: [
      "Problem, Action, Result",
      "Plan, Analyze, Report",
      "Perform, Assess, Review",
      "Process, Action, Response"
    ],
    correctIndex: 0,
    explanation: "PAR (Problem, Action, Result) is a shortened, punchy variation of the STAR framework.",
    category: "Interview"
  },
  {
    id: 52,
    question: "How soon after an interview should you send a thank-you email?",
    options: [
      "Within 24 hours of the interview",
      "1 week later",
      "Immediately during the interview call",
      "Thank you emails are outdated"
    ],
    correctIndex: 0,
    explanation: "A personalized thank-you note sent within 24 hours keeps you top-of-mind and shows courtesy.",
    category: "Interview"
  },
  {
    id: 53,
    question: "What should be included in an interview thank-you email?",
    options: [
      "Appreciation for their time, reference to a specific topic discussed, and renewed enthusiasm for the role",
      "A request for salary negotiation",
      "Corrections to mistakes you made in the interview",
      "Attach your full portfolio again"
    ],
    correctIndex: 0,
    explanation: "Referencing a specific conversation topic reinforces that you listened actively and engaged.",
    category: "Interview"
  },
  {
    id: 54,
    question: "When asked 'Why are you leaving your current job?', what is the best strategy?",
    options: [
      "Focus on seeking new opportunities for growth, learning, and fresh challenges",
      "Complain about bad management or toxic colleagues",
      "Say 'For a higher salary only'",
      "Refuse to answer"
    ],
    correctIndex: 0,
    explanation: "Frame transitions positively toward future growth rather than backward complaints.",
    category: "Interview"
  },
  {
    id: 55,
    question: "What is the best way to handle salary expectation questions in early interview rounds?",
    options: [
      "Provide a researched realistic range based on market rates or ask for the budget range for the position",
      "State one rigid ultra-high number",
      "Say 'I don't care about money'",
      "Demand immediate contract terms"
    ],
    correctIndex: 0,
    explanation: "Providing a competitive market-researched salary range keeps negotiations flexible.",
    category: "Interview"
  },
  {
    id: 56,
    question: "What does 'CAR' stand for in interview frameworks?",
    options: [
      "Context, Action, Result",
      "Challenge, Assessment, Resolution",
      "Career, Achievement, Responsibility",
      "Concept, Application, Review"
    ],
    correctIndex: 0,
    explanation: "CAR stands for Context, Action, Result — another streamlined behavioral response framework.",
    category: "Interview"
  },
  {
    id: 57,
    question: "What body language sign indicates active listening during an interview?",
    options: [
      "Nodding, maintaining comfortable eye contact, and leaning slightly forward",
      "Crossing arms tightly and leaning back",
      "Checking your smartwatch repeatedly",
      "Staring blankly without nodding"
    ],
    correctIndex: 0,
    explanation: "Attentive posture and natural nodding show engagement and respectful active listening.",
    category: "Interview"
  },
  {
    id: 58,
    question: "Why should you prepare specific numbers and metrics for interview stories?",
    options: [
      "Quantified achievements (percentages, revenue, team size) make accomplishments concrete and memorable",
      "Numbers make answers sound longer",
      "Interviews require math tests",
      "It fills silence"
    ],
    correctIndex: 0,
    explanation: "Concrete numbers (e.g., 'managed $50k budget, cut load times by 40%') provide undeniable proof of impact.",
    category: "Interview"
  },
  {
    id: 59,
    question: "How can you reduce nervous energy before an interview?",
    options: [
      "Practice mock interviews out loud, do deep breathing exercises, and review your key story points",
      "Drink 5 cups of espresso",
      "Cram new information 1 minute before",
      "Avoid all preparation"
    ],
    correctIndex: 0,
    explanation: "Rehearsing aloud and controlled breathing calm the nervous system and build fluid recall.",
    category: "Interview"
  },
  {
    id: 60,
    question: "What is a 'Case Interview' commonly used for?",
    options: [
      "Evaluating business problem-solving, analytical thinking, and structured logic under pressure",
      "Testing legal software compliance",
      "Scanning police background records",
      "Checking resume layout styles"
    ],
    correctIndex: 0,
    explanation: "Case interviews test how logically you break down complex, open-ended business problems.",
    category: "Interview"
  },
  {
    id: 61,
    question: "What should you do if an interviewer interrupts you while answering?",
    options: [
      "Pause politely, listen to their direction, and adapt your response accordingly",
      "Raise your voice to finish your point",
      "Stop speaking completely and refuse to continue",
      "Ignore the interruption"
    ],
    correctIndex: 0,
    explanation: "Adapting gracefully to interruptions demonstrates flexibility and respectful communication.",
    category: "Interview"
  },
  {
    id: 62,
    question: "When asked 'Where do you see yourself in 5 years?', what is the hiring manager assessing?",
    options: [
      "Your career ambition, realistic expectations, and whether the role fits your long-term commitment",
      "Whether you plan to take the manager's job",
      "Your retirement plans",
      "Your personal travel wish list"
    ],
    correctIndex: 0,
    explanation: "Employers want to see that the role aligns with your career trajectory and growth aspirations.",
    category: "Interview"
  },
  {
    id: 63,
    question: "What dress code is appropriate for a video interview at a corporate firm?",
    options: [
      "Business professional or smart business attire appropriate for the company culture",
      "Casual pajamas since only top half is visible",
      "Gym athletic wear",
      "Costume outfits"
    ],
    correctIndex: 0,
    explanation: "Dressing professionally sets a confident mindset and signals respect for the company.",
    category: "Interview"
  },
  {
    id: 64,
    question: "What is the best way to handle a panel interview with multiple interviewers?",
    options: [
      "Address the person who asked the question initially, make eye contact with everyone while answering, and finish back with the asker",
      "Only look at the highest-ranking person in the room",
      "Look at the table or wall",
      "Answer each person individually in separate speeches"
    ],
    correctIndex: 0,
    explanation: "Engaging the entire panel builds rapport across all key decision-makers.",
    category: "Interview"
  },
  {
    id: 65,
    question: "What should you do after sending a follow-up email if you don't hear back after a week?",
    options: [
      "Send one polite final check-in after 7-10 business days, then continue focusing on other job applications",
      "Call the CEO daily until they reply",
      "Post angry comments on company social media",
      "Give up applying to jobs entirely"
    ],
    correctIndex: 0,
    explanation: "One courteous follow-up keeps doors open while maintaining your momentum elsewhere.",
    category: "Interview"
  },

  // --- NETWORKING & LINKEDIN (66 - 80) ---
  {
    id: 66,
    question: "What is the primary section recruiters search on LinkedIn to discover candidates?",
    options: [
      "Headline, About summary, Job titles, and Key skills",
      "Profile background banner picture",
      "Number of connections",
      "Saved post bookmarks"
    ],
    correctIndex: 0,
    explanation: "LinkedIn search algorithms prioritize relevant keywords in your Headline, Titles, and Skills.",
    category: "Networking"
  },
  {
    id: 67,
    question: "What is a strong LinkedIn headline formula?",
    options: [
      "[Target Job Title] | [Core Specialization/Tech Stack] | [Value Delivered/Achievement]",
      "'Unemployed and seeking any job urgently'",
      "'Guru / Ninja / Wizard of code'",
      "Leaving the headline blank"
    ],
    correctIndex: 0,
    explanation: "A structured headline clearly communicates your role, skills, and value proposition.",
    category: "Networking"
  },
  {
    id: 68,
    question: "When sending a LinkedIn connection request to someone you haven't met, what should you do?",
    options: [
      "Add a personalized note explaining why you'd like to connect or referencing shared interests",
      "Send blank connection requests to 500 random people",
      "Ask for a job referral in the first message",
      "Demand immediate resume review"
    ],
    correctIndex: 0,
    explanation: "Personalized connection notes drastically increase acceptance rates and start warm conversations.",
    category: "Networking"
  },
  {
    id: 69,
    question: "What is an 'Informational Interview'?",
    options: [
      "A 15-20 min informal conversation with an industry professional to learn about their career path and advice",
      "An official job interview with HR",
      "A coding test session",
      "A sales pitch presentation"
    ],
    correctIndex: 0,
    explanation: "Informational interviews build genuine industry relationships without putting pressure on job offers.",
    category: "Networking"
  },
  {
    id: 70,
    question: "What feature on LinkedIn notifies recruiters that you are actively seeking job opportunities?",
    options: [
      "'Open to Work' feature (visible to recruiters only or public badge)",
      "LinkedIn Premium gold badge",
      "Posting status updates hourly",
      "Changing profile name"
    ],
    correctIndex: 0,
    explanation: "Enabling 'Open to Work' highlights your profile in recruiter search tools like LinkedIn Recruiter.",
    category: "Networking"
  },
  {
    id: 71,
    question: "Why are LinkedIn recommendations from former colleagues or managers valuable?",
    options: [
      "They act as social proof and public endorsements of your work ethics and abilities",
      "They automatically rank your profile #1 in search",
      "They are required to unlock your account",
      "They hide past employment gaps"
    ],
    correctIndex: 0,
    explanation: "Authentic recommendations validate your skills and boost recruiter trust.",
    category: "Networking"
  },
  {
    id: 72,
    question: "How often should you post or engage on LinkedIn to build visibility?",
    options: [
      "Consistently engaging weekly through insightful comments, sharing industry articles, or posting learnings",
      "Posting 50 times a day",
      "Once every 3 years",
      "Only when actively unemployed"
    ],
    correctIndex: 0,
    explanation: "Consistent weekly engagement keeps your professional network active and visible to recruiters.",
    category: "Networking"
  },
  {
    id: 73,
    question: "What should you avoid sending in a cold message to a company leader?",
    options: [
      "An aggressive message demanding an immediate job interview without prior connection or rapport",
      "A concise polite greeting expressing appreciation for their work",
      "A brief question about their team culture",
      "A link to your online portfolio"
    ],
    correctIndex: 0,
    explanation: "Building rapport and showing authentic interest comes before asking for favors or jobs.",
    category: "Networking"
  },
  {
    id: 74,
    question: "What is 'Hidden Job Market'?",
    options: [
      "Job opportunities filled through networking, internal referrals, or direct outreach before being publicly posted",
      "Jobs on dark web sites",
      "Illegal job postings",
      "Fake job advertisements"
    ],
    correctIndex: 0,
    explanation: "Up to 70-80% of roles are filled through referrals and networking before public job boards.",
    category: "Networking"
  },
  {
    id: 75,
    question: "What is an effective way to ask a contact for an employee referral?",
    options: [
      "Share why you admire the company, highlight matching qualifications, and ask if they feel comfortable referring you",
      "Send a message saying 'Refer me now please'",
      "Attach your resume with no text",
      "Promise money for referrals"
    ],
    correctIndex: 0,
    explanation: "Making it easy for the referrer by sharing clear context and relevant highlights yields high success.",
    category: "Networking"
  },
  {
    id: 76,
    question: "What is personal branding in the context of professional careers?",
    options: [
      "The unique combination of skills, experience, and reputation you present online and offline",
      "Designing a custom personal logo",
      "Buying custom domain names",
      "Wearing matching outfits"
    ],
    correctIndex: 0,
    explanation: "Personal branding is how your expertise and professional value are recognized across your industry.",
    category: "Networking"
  },
  {
    id: 77,
    question: "What should you do after attending a career fair or networking event?",
    options: [
      "Follow up on LinkedIn within 24-48 hours mentioning a specific detail from your event conversation",
      "Wait 1 month before sending contact requests",
      "Throw away collected business cards",
      "Send generic mass marketing emails"
    ],
    correctIndex: 0,
    explanation: "Timely follow-ups lock in initial event connections while conversations are fresh.",
    category: "Networking"
  },
  {
    id: 78,
    question: "How can joining professional associations or industry groups aid your career?",
    options: [
      "Provides access to industry events, specialized training, mentorship, and unadvertised job openings",
      "It guarantees immediate salary increases",
      "It replaces higher education",
      "It gives free software licenses"
    ],
    correctIndex: 0,
    explanation: "Industry groups foster specialized networking and ongoing professional development.",
    category: "Networking"
  },
  {
    id: 79,
    question: "What is the benefit of publishing original articles or posts about your projects on LinkedIn?",
    options: [
      "Demonstrates thought leadership, practical skills, and continuous learning to peers and recruiters",
      "Increases your account follower limit",
      "Replaces having a resume",
      "Bypasses background checks"
    ],
    correctIndex: 0,
    explanation: "Sharing project breakdowns or lessons learned establishes domain credibility.",
    category: "Networking"
  },
  {
    id: 80,
    question: "What is 'Elevator Pitch' length in networking events?",
    options: [
      "30 to 60 seconds",
      "5 to 10 minutes",
      "1 single word",
      "30 minutes"
    ],
    correctIndex: 0,
    explanation: "An elevator pitch introduces who you are, what you do, and your core value in under 60 seconds.",
    category: "Networking"
  },

  // --- WORKPLACE SKILLS & CAREER GROWTH (81 - 100) ---
  {
    id: 81,
    question: "What is Emotional Intelligence (EQ) in the workplace?",
    options: [
      "The ability to perceive, understand, manage, and express emotions constructively in professional interactions",
      "IQ score measurement",
      "Technical programming efficiency",
      "Working without breaks"
    ],
    correctIndex: 0,
    explanation: "High EQ drives leadership success, conflict resolution, and strong workplace collaboration.",
    category: "Workplace"
  },
  {
    id: 82,
    question: "What is the 'Pomodoro Technique' for productivity?",
    options: [
      "Working in focused 25-minute sprints followed by a 5-minute break",
      "Eating pasta during lunch breaks",
      "Working 8 hours non-stop",
      "Scheduling meetings every hour"
    ],
    correctIndex: 0,
    explanation: "Pomodoro structured intervals boost focus and prevent cognitive fatigue.",
    category: "Workplace"
  },
  {
    id: 83,
    question: "What does SMART stand for in professional goal setting?",
    options: [
      "Specific, Measurable, Achievable, Relevant, Time-bound",
      "Simple, Meaningful, Actionable, Realistic, Timely",
      "Strategic, Managed, Automated, Rapid, Tested",
      "Standard, Maintained, Approved, Reviewed, Tracked"
    ],
    correctIndex: 0,
    explanation: "SMART goals clarify expectations and ensure tracked, achievable milestones.",
    category: "Workplace"
  },
  {
    id: 84,
    question: "How should constructive feedback from a manager or peer be received?",
    options: [
      "Listen actively without immediate defensiveness, seek clarity, and create an action plan to grow",
      "Argue back immediately",
      "Ignore feedback completely",
      "Resign on the spot"
    ],
    correctIndex: 0,
    explanation: "Receiving feedback constructively reflects maturity and commitment to continuous growth.",
    category: "Workplace"
  },
  {
    id: 85,
    question: "What is 'Upward Communication' in organizational dynamics?",
    options: [
      "Communicating status updates, risks, and recommendations clearly from employee to management",
      "Shouting in meetings",
      "Sending emails to external clients",
      "Communicating with junior interns"
    ],
    correctIndex: 0,
    explanation: "Clear upward communication keeps leaders informed and helps unblock team challenges.",
    category: "Workplace"
  },
  {
    id: 86,
    question: "What is the primary benefit of cross-functional collaboration?",
    options: [
      "Combines diverse skills across engineering, product, marketing, and design to build superior solutions",
      "Reduces total company staff count",
      "Eliminates all workplace meetings",
      "Separates departments permanently"
    ],
    correctIndex: 0,
    explanation: "Cross-functional teamwork breaks down silos and fosters innovation.",
    category: "Workplace"
  },
  {
    id: 87,
    question: "What does the 80/20 rule (Pareto Principle) suggest in time management?",
    options: [
      "80% of key results come from 20% of high-impact efforts",
      "Work 80 hours a week and rest 20 hours",
      "Spend 80% time in meetings and 20% working",
      "Hire 80% seniors and 20% juniors"
    ],
    correctIndex: 0,
    explanation: "Prioritizing the top 20% high-leverage tasks yields 80% of total productivity.",
    category: "Workplace"
  },
  {
    id: 88,
    question: "How should you approach handling a workplace conflict professionally?",
    options: [
      "Focus on the issue (not personalities), listen to perspectives, and negotiate a collaborative solution",
      "Gossip about the conflict with other teams",
      "Pretend the problem does not exist",
      "Send aggressive all-caps emails"
    ],
    correctIndex: 0,
    explanation: "Addressing conflicts directly and objectively fosters mutual respect and resolution.",
    category: "Workplace"
  },
  {
    id: 89,
    question: "What is a '1-on-1' meeting with your manager primarily intended for?",
    options: [
      "Discussing alignment, career progression, feedback, challenges, and personal development",
      "Getting micromanaged on daily tasks only",
      "Social chatting only",
      "Annual salary review"
    ],
    correctIndex: 0,
    explanation: "Regular 1-on-1s align goals, address blockers, and accelerate career growth.",
    category: "Workplace"
  },
  {
    id: 90,
    question: "What is 'Psychological Safety' in team environments?",
    options: [
      "A culture where team members feel safe to take risks, ask questions, and admit mistakes without fear of humiliation",
      "Installing office security cameras",
      "Having ergonomic chairs",
      "Providing free health insurance"
    ],
    correctIndex: 0,
    explanation: "High-performing teams thrive when members can speak up and innovate without fear.",
    category: "Workplace"
  },
  {
    id: 91,
    question: "What is 'Active Listening'?",
    options: [
      "Fully concentrating, understanding, responding, and remembering what the speaker is communicating",
      "Thinking about your reply while others speak",
      "Multitasking while listening",
      "Interrupting with immediate answers"
    ],
    correctIndex: 0,
    explanation: "Active listening builds trust and ensures clear, accurate comprehension.",
    category: "Workplace"
  },
  {
    id: 92,
    question: "What should you do when taking ownership of a mistake at work?",
    options: [
      "Acknowledge the mistake early, explain the root cause briefly, and present a solution plan",
      "Blame another team member",
      "Hide the mistake and hope no one notices",
      "Make excuses"
    ],
    correctIndex: 0,
    explanation: "Prompt accountability paired with a solution plan builds strong professional credibility.",
    category: "Workplace"
  },
  {
    id: 93,
    question: "What is an effective way to showcase leadership skills before officially being a manager?",
    options: [
      "Mentoring new peers, taking initiative on messy problems, and driving projects to completion",
      "Giving orders to colleagues",
      "Critiquing others publicly",
      "Refusing entry-level tasks"
    ],
    correctIndex: 0,
    explanation: "Leadership is demonstrated through proactive initiative, mentorship, and accountability.",
    category: "Workplace"
  },
  {
    id: 94,
    question: "What does 'Imposter Syndrome' mean?",
    options: [
      "Doubting your abilities and feeling like a fraud despite documented success and competence",
      "Faking a job resume",
      "Impersonating another employee",
      "A technical security risk"
    ],
    correctIndex: 0,
    explanation: "Imposter syndrome is common; tracking factual achievements helps overcome self-doubt.",
    category: "Workplace"
  },
  {
    id: 95,
    question: "Why is continuous upskilling important in today's job market?",
    options: [
      "Technology and industry standards evolve rapidly; continuous learning maintains your competitive edge",
      "To accumulate course completion certificates",
      "It is required by labor laws",
      "Upskilling is only for beginners"
    ],
    correctIndex: 0,
    explanation: "Lifelong learning ensures adaptability as AI and industry tools advance.",
    category: "Workplace"
  },
  {
    id: 96,
    question: "What is 'Time Blocking'?",
    options: [
      "Scheduling specific blocks of time on your calendar dedicated solely to deep focused tasks",
      "Blocking people from your calendar",
      "Refusing to work over 4 hours",
      "Clocking in late"
    ],
    correctIndex: 0,
    explanation: "Time blocking protects focus periods from meeting interruptions.",
    category: "Workplace"
  },
  {
    id: 97,
    question: "What is the purpose of a Performance Review?",
    options: [
      "To evaluate past achievements against set goals, give mutual feedback, and plan future growth",
      "To criticize employees",
      "To replace daily communication",
      "To reduce company budget"
    ],
    correctIndex: 0,
    explanation: "Performance reviews benchmark accomplishments and chart future growth and compensation.",
    category: "Workplace"
  },
  {
    id: 98,
    question: "What is 'Asynchronous Communication' in remote work?",
    options: [
      "Communication that does not require an immediate real-time response (e.g. detailed docs, Slack updates)",
      "Communication over phone calls only",
      "In-person daily meetings",
      "Communication with audio delay"
    ],
    correctIndex: 0,
    explanation: "Async communication gives remote teams deep focus time and documented clarity.",
    category: "Workplace"
  },
  {
    id: 99,
    question: "What does 'Scope Creep' mean in project management?",
    options: [
      "Continuous uncontrolled expansion of project requirements beyond original limits",
      "Project deadline moving sooner",
      "A project manager watching team tasks",
      "Reducing project features"
    ],
    correctIndex: 0,
    explanation: "Managing scope creep prevents burnout and budget overruns.",
    category: "Workplace"
  },
  {
    id: 100,
    question: "What is the single most valuable habit for career advancement?",
    options: [
      "Consistently delivering high-quality results, communicating proactively, and continuing to learn",
      "Working 100 hours a week without sleeping",
      "Relying solely on luck",
      "Changing jobs every month"
    ],
    correctIndex: 0,
    explanation: "Reliable excellence, proactive communication, and continuous learning build sustainable career mastery.",
    category: "Workplace"
  }
];

/**
 * Utility to pick N unique random questions from the 100 pool,
 * AND shuffle the 4 options for each question so the correct answer is randomized (A, B, C, or D).
 */
export function getRandomQuestions(count = 10): QuizQuestion[] {
  const shuffledPool = [...CAREER_QUIZ_QUESTIONS];
  for (let i = shuffledPool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffledPool[i]!;
    shuffledPool[i] = shuffledPool[j]!;
    shuffledPool[j] = temp;
  }

  const selected = shuffledPool.slice(0, count);

  return selected.map((q) => {
    const originalCorrectText = q.options[q.correctIndex]!;
    const shuffledOptions = [...q.options] as [string, string, string, string];

    // Fisher-Yates shuffle the 4 options array safely
    for (let i = shuffledOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tempOpt = shuffledOptions[i]!;
      shuffledOptions[i] = shuffledOptions[j]!;
      shuffledOptions[j] = tempOpt;
    }

    const newCorrectIndex = shuffledOptions.indexOf(originalCorrectText);

    return {
      ...q,
      options: shuffledOptions,
      correctIndex: newCorrectIndex >= 0 ? newCorrectIndex : 0,
    };
  });
}
